import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous, object = authenticated
  const { setLang } = useLanguage();

  const checkAuth = useCallback(async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Skip /me check while AuthCallback is handling the OAuth session exchange
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.preferred_language) setLang(data.preferred_language);
    } catch {
      setUser(false);
    }
  }, [setLang]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    if (data?.preferred_language) setLang(data.preferred_language);
    return data;
  }, [setLang]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {});
    } catch (err) {
      // Logout should always succeed locally even if the API call failed (e.g. expired token).
      console.warn("Logout request failed:", formatApiError(err));
    }
    setUser(false);
  }, []);

  const setPreferredLanguage = useCallback(async (code) => {
    try {
      const { data } = await api.post("/auth/preferred-language", { preferred_language: code });
      setUser(data);
    } catch (err) {
      // Anonymous users have no profile to update — only log when authenticated.
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("Could not persist language:", formatApiError(err));
      }
    }
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, register, logout, checkAuth, setPreferredLanguage, formatApiError }),
    [user, login, register, logout, checkAuth, setPreferredLanguage]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
